// src/pages/admin/tabs/properties/CommercialRentPropertyCreatePage.jsx

import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AdminAPI } from '../../../../api/admin';
import { PublicAPI } from '../../../../api/public';
import { Card, Row2, Row3, Field, Actions, Btn, Collapse } from '../../ui';
import RichTextWithTemplates from '../../components/RichTextWithTemplates';
import SpecializedPropertyMediaManager from '../../components/SpecializedPropertyMediaManager';
import SpecializedUnitsPanel from '../../components/SpecializedUnitsPanel';
import SpecializedManagersPanel from '../../components/SpecializedManagersPanel';
import SpecializedPaymentPlansPanel from '../../components/SpecializedPaymentPlansPanel';
import SpecializedAmenitiesPanel from '../../components/SpecializedAmenitiesPanel';
import MapPicker from '../../components/MapPicker';

// slugify с транслитерацией кириллицы
const slugify = (s = '') => {
  // Карта транслитерации кириллицы
  const cyrillicMap = {
    'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo',
    'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
    'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
    'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch',
    'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya'
  };
  
  return s.toString()
    .toLowerCase()
    .split('')
    .map(char => cyrillicMap[char] || char)
    .join('')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')
    .slice(0, 80);
};

// валидация
function validate(form) {
  const errors = {};
  if (!(form.title_ru && form.title_ru.trim()) && !(form.title_en && form.title_en.trim())) {
    errors.title_ru = 'Укажите название (RU или EN)';
  }
  if (form.price_base === '' || form.price_base === null || form.price_base === undefined || Number(form.price_base) < 0) errors.price_base = 'Укажите цену';
  if (!form.property_type_id) errors.property_type_id = 'Укажите тип объекта';
  if (!form.deal_type_id) errors.deal_type_id = 'Укажите тип сделки';
  if (!form.region_id) errors.region_id = 'Выберите регион';
  if (!form.district_id) errors.district_id = 'Выберите район';
  if (!form.address) errors.address = 'Укажите адрес';
  // Проверка диапазона координат, если заданы
  if (form.lat && (Number(form.lat) < -90 || Number(form.lat) > 90)) errors.lat = 'lat должен быть от -90 до 90';
  if (form.lng && (Number(form.lng) < -180 || Number(form.lng) > 180)) errors.lng = 'lng должен быть от -180 до 180';
  return errors;
}
const hasErrors = (errs) => Object.keys(errs).length > 0;

export default function CommercialRentPropertyCreatePage() {
  const { id } = useParams(); // "new" или число
  const isNew = !id || id === 'new';
  const nav = useNavigate();

  const goToEdit = (propId) => nav(`/admin/commercial-rent/${propId}`);

  const [regions, setRegions] = React.useState([]);
  const [districts, setDistricts] = React.useState([]);
  const [propertyTypes, setPropertyTypes] = React.useState([]);
  const [dealTypes, setDealTypes] = React.useState([]);

  const empty = {
    id: null,
    title_ru: '',
    title_en: '',
    slug: '',
    price_base: '',
    price_currency: 'AED',
    price_max: '',
    price_per_m2_min: '',
    price_per_m2_max: '',
    // локация
    address: '',
    lat: '',
    lng: '',
    // застройщик
    developer_name_ru: '',
    developer_name_en: '',
    region_id: '',
    district_id: '',
    property_type_id: '',
    deal_type_id: '',
    area_total_min: '',
    area_total_max: '',
    // специфичные поля для коммерческой аренды
    commercial_type: '',
    floor_min: '',
    floor_max: '',
    ceiling_height: '',
    parking_spaces: '',
    is_furnished: false,
    installment_available: false,
    handover_quarter: '',
    handover_year: '',
    is_featured: false,
    is_investment: false,
    is_homepage: false,
    is_active: true,
    status: 'active',
    description_ru: '',
    description_en: '',
    meta_title_ru: '',
    meta_title_en: '',
    meta_description_ru: '',
    meta_description_en: '',
  };
  const [form, setForm] = React.useState(empty);
  const [ranges, setRanges] = React.useState({ bedrooms: { min: null, max: null }, area: { min: null, max: null } });
  const [msg, setMsg] = React.useState('');
  const [okToast, setOkToast] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [savedModal, setSavedModal] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [loadingForm, setLoadingForm] = React.useState(false);

  // валидация
  const [touched, setTouched] = React.useState({});
  const [submitted, setSubmitted] = React.useState(false);
  const errors = validate(form);
  const showErr = (name) => !!errors[name] && (touched[name] || submitted);
  const markTouched = (name) => setTouched((t) => ({ ...t, [name]: true }));

  // refs
  const refs = {
    title_ru: React.useRef(null),
    title_en: React.useRef(null),
    slug: React.useRef(null),
    price_base: React.useRef(null),
    property_type_id: React.useRef(null),
    deal_type_id: React.useRef(null),
    region_id: React.useRef(null),
    district_id: React.useRef(null),
    address: React.useRef(null),
    lat: React.useRef(null),
    lng: React.useRef(null),
  };
  const errStyle = (name) =>
    showErr(name)
      ? {
          borderColor: '#e66',
          boxShadow: '0 0 0 3px rgba(230,102,102,.15)',
          background: 'rgba(230,102,102,.05)',
        }
      : {};

  // словари + валюты
  const [currencies, setCurrencies] = React.useState([]);
  const [previousCurrency, setPreviousCurrency] = React.useState('');

  React.useEffect(() => {
    (async () => {
      try {
        const [rResp, dResp, ptResp, dtResp, navResp] = await Promise.all([
          PublicAPI.regions(),
          PublicAPI.districts(),
          PublicAPI.propertyTypes(),
          PublicAPI.dealTypes(),
          PublicAPI.navigation(),
        ]);
        setRegions(rResp?.regions || []);
        setDistricts(dResp?.districts || dResp?.items || []);
        setPropertyTypes(ptResp?.property_types || []);
        setDealTypes(dtResp?.deal_types || []);
        setCurrencies(navResp?.currencies || []);
      } catch (e) {
        console.error('Failed to load dictionaries', e);
        setRegions([]);
        setDistricts([]);
        setPropertyTypes([]);
        setDealTypes([]);
        setCurrencies([]);
      }
    })();
  }, []);

  // автогенерация slug
  React.useEffect(() => {
    if (isNew) return; // при создании slug генерирует бэкенд
    const src = form.title_ru || form.title_en;
    if (src && !touched.slug && !form.slug) {
      setForm((f) => ({ ...f, slug: slugify(src) }));
    }
  }, [isNew, form.title_ru, form.title_en, form.slug, touched.slug]);

  // загрузка текущего объекта (редактирование)
  React.useEffect(() => {
    (async () => {
      if (isNew) return;
      setLoadingForm(true);
      setMsg('');
      try {
        const resp = await AdminAPI.getCommercialRentProperty(Number(id), { locale: 'ru' });
        const p = resp?.property || resp || {};

        const loadedCurrency = p.price_currency || p.currency?.code || 'AED';
        setPreviousCurrency(loadedCurrency);

        setForm({
          id: p.id ?? null,
          title_ru: p.title_ru ?? p.title ?? '',
          title_en: p.title_en ?? '',
          slug: p.slug ?? '',
          price_base: p.price_base ?? p.price ?? p.price_aed ?? '',
          price_currency: loadedCurrency,
          price_max: p.price_max ?? '',
          price_per_m2_min: p.price_per_m2_min ?? '',
          price_per_m2_max: p.price_per_m2_max ?? '',
          // локация
          address: p.address ?? p.city ?? '',
          lat: p.lat ?? '',
          lng: p.lng ?? '',
          developer_name_ru: p.developer_name_ru ?? p.developer ?? '',
          developer_name_en: p.developer_name_en ?? '',
          region_id: p.region_id ?? p.region?.id ?? '',
          district_id: p.district_id ?? p.district?.id ?? '',
          property_type_id: p.property_type_id ?? p.property_type?.id ?? '',
          deal_type_id: p.deal_type_id ?? p.deal_type?.id ?? '',
          area_total_min: (p.area_total_min != null ? p.area_total_min : (p.area_total_range?.min ?? '')),
          area_total_max: (p.area_total_max != null ? p.area_total_max : (p.area_total_range?.max ?? '')),
          commercial_type: p.commercial_type ?? '',
          floor_min: p.floor_min ?? '',
          floor_max: p.floor_max ?? '',
          ceiling_height: p.ceiling_height ?? '',
          parking_spaces: p.parking_spaces ?? '',
          is_furnished: !!p.is_furnished,
          installment_available: !!p.installment_available,
          handover_quarter: p.handover_quarter ?? '',
          handover_year: p.handover_year ?? '',
          is_featured: !!p.is_featured,
          is_investment: !!p.is_investment,
          is_homepage: !!p.is_homepage,
          is_active: p.is_active ?? true,
          status: p.status || 'active',
          description_ru: p.description_ru ?? p.description ?? '',
          description_en: p.description_en ?? '',
          meta_title_ru: p.meta_title_ru ?? '',
          meta_title_en: p.meta_title_en ?? '',
          meta_description_ru: p.meta_description_ru ?? '',
          meta_description_en: p.meta_description_en ?? '',
          extra: (() => {
            try { return JSON.stringify(p.extra || {}, null, 2); }
            catch { return '{}'; }
          })(),
        });

        setRanges({
          bedrooms: {
            min: (p?.bedrooms_range && (p.bedrooms_range.min ?? null)) ?? null,
            max: (p?.bedrooms_range && (p.bedrooms_range.max ?? null)) ?? null,
          },
          area: {
            min: (p?.area_total_range && (p.area_total_range.min ?? null)) ?? null,
            max: (p?.area_total_range && (p.area_total_range.max ?? null)) ?? null,
          },
        });

        setTouched({});
        setSubmitted(false);
      } catch (e) {
        setMsg(e.message || 'Не удалось загрузить объект');
      } finally {
        setLoadingForm(false);
      }
    })();
  }, [isNew, id]);

  // ОТКЛЮЧЕНО: Автоматическая конвертация цен при смене валюты
  // Теперь цены сохраняются как введено пользователем
  React.useEffect(() => {
    // Просто обновляем previousCurrency без конвертации
    if (form.price_currency && form.price_currency !== previousCurrency) {
      setPreviousCurrency(form.price_currency);
    }
  }, [form.price_currency, previousCurrency]);

  const toPayload = () => {
    // Generate slug for new properties if not provided
    let slug = form.slug;
    if (isNew && !slug) {
      // Приоритет английскому названию для slug
      const title = form.title_en || form.title_ru || '';
      if (title) {
        slug = slugify(title);
      }
    }
    
    return {
      title_ru: form.title_ru || undefined,
      title_en: form.title_en || undefined,
      slug: slug || undefined,
      price_base: form.price_base ? Number(form.price_base) : undefined,
      price_currency: form.price_currency || 'AED',
      price_max: form.price_max ? Number(form.price_max) : null,
      price_per_m2_min: form.price_per_m2_min ? Number(form.price_per_m2_min) : null,
      price_per_m2_max: form.price_per_m2_max ? Number(form.price_per_m2_max) : null,

      // важно: сервер ждёт address, lat/lng
      address: form.address || undefined,
      lat: form.lat !== '' ? Number(form.lat) : undefined,
      lng: form.lng !== '' ? Number(form.lng) : undefined,

      // застройщик: если заполнено только одно поле — отправим developer (в оба языка)
      ...(form.developer_name_ru && form.developer_name_en
        ? { developer_name_ru: form.developer_name_ru, developer_name_en: form.developer_name_en }
        : form.developer_name_ru
          ? { developer: form.developer_name_ru }
          : form.developer_name_en
            ? { developer: form.developer_name_en }
            : {}),
      region_id: form.region_id ? Number(form.region_id) : undefined,
      district_id: form.district_id ? Number(form.district_id) : undefined,
      property_type_id: form.property_type_id ? Number(form.property_type_id) : undefined,
      deal_type_id: form.deal_type_id ? Number(form.deal_type_id) : undefined,
      area_total_min: form.area_total_min !== '' ? Number(form.area_total_min) : undefined,
      area_total_max: form.area_total_max !== '' ? Number(form.area_total_max) : undefined,
      commercial_type: form.commercial_type || undefined,
      floor_min: form.floor_min !== '' ? Number(form.floor_min) : undefined,
      floor_max: form.floor_max !== '' ? Number(form.floor_max) : undefined,
      ceiling_height: form.ceiling_height !== '' ? Number(form.ceiling_height) : undefined,
      parking_spaces: form.parking_spaces !== '' ? Number(form.parking_spaces) : undefined,
      is_furnished: !!form.is_furnished,
      installment_available: !!form.installment_available,
      handover_quarter: form.handover_quarter || undefined,
      handover_year: form.handover_year ? Number(form.handover_year) : undefined,
      is_featured: !!form.is_featured,
      is_investment: !!form.is_investment,
      is_active: !!form.is_active,
      status: form.status || undefined,
      description_ru: form.description_ru || undefined,
      description_en: form.description_en || undefined,
      meta_title_ru: form.meta_title_ru || undefined,
      meta_title_en: form.meta_title_en || undefined,
      meta_description_ru: form.meta_description_ru || undefined,
      meta_description_en: form.meta_description_en || undefined,
    };
  };

  const scrollToFirstError = () => {
    const order = ['title_ru','title_en','slug','price_base','property_type_id','deal_type_id','region_id','district_id','address','lat','lng'];
    for (const key of order) {
      if (errors[key] && refs[key]?.current) {
        refs[key].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        try { refs[key].current.focus(); } catch {}
        break;
      }
    }
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setSubmitted(true);
    setMsg('');

    if (!isNew && (!form.slug || !form.slug.trim()) && form.title?.trim()) {
      // Автогенерация slug для совместимости с текущим бэкендом,
      // если сервер ещё не генерирует его сам
      const auto = slugify(form.title);
      if (auto) setForm((f) => ({ ...f, slug: auto }));
    }

    const errs = validate({ ...form });
    if (hasErrors(errs)) {
      setMsg('Заполните обязательные поля');
      scrollToFirstError();
      return;
    }

    setSaving(true);
    try {
      const payload = toPayload();
      let propertyId = isNew ? null : form.id || Number(id);

      if (isNew) {
        const resp = await AdminAPI.createCommercialRentProperty(payload);
        propertyId = resp?.property?.id || resp?.id;
        if (!propertyId) throw new Error('Не получили ID созданного объекта');
        // применим флаги, зависящие от отдельных эндпоинтов
        try{
          if (form.is_featured) await AdminAPI.setFeaturedCommercialRentProperty(propertyId, true);
          if (form.is_homepage) await AdminAPI.homepageSetCommercialRent(propertyId, true);
        }catch(e){ /* не критично */ }
        setMsg('Коммерческая аренда создана');
        setOkToast('Коммерческая аренда создана');
        setSavedModal(true);
        setTimeout(()=>{ 
          setOkToast(''); 
          setSavedModal(false); 
          setMsg(''); 
          goToEdit(propertyId); 
        }, 1800);
        return;
      } else {
        await AdminAPI.updateCommercialRentProperty(propertyId, payload);
        setMsg('Изменения сохранены');
        setOkToast('Изменения сохранены');
        setSavedModal(true);
        setTimeout(()=>{ setOkToast(''); setSavedModal(false); setMsg(''); }, 1800);
      }
    } catch (e2) {
      setMsg(e2.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const districtsForForm = form.region_id
    ? districts.filter((d) => String(d.region_id ?? '') === String(form.region_id))
    : districts;

  return (
    <>
      {/* Модальное окно: сохраняем / сохранено */}
      {(saving || savedModal) && (
        <div style={{
          position:'fixed', inset:0, zIndex:4000, background:'rgba(0,0,0,.35)',
          display:'flex', alignItems:'center', justifyContent:'center'
        }} aria-live="polite" role="status">
          <div style={{
            background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:16,
            boxShadow:'var(--shadow)', padding:'18px 20px', minWidth:280, textAlign:'center'
          }}>
            <div style={{fontWeight:800, marginBottom:6}}>
              {saving ? (isNew ? 'Создаём…' : 'Сохраняем…') : 'Сохранено'}
            </div>
            <div style={{opacity:.8, fontSize:13}}>
              {saving ? 'Пожалуйста, подождите' : (isNew ? 'Объект создан' : 'Изменения сохранены')}
            </div>
            {saving && (
              <div style={{marginTop:12, display:'grid', placeItems:'center'}}>
                <div style={{
                  width:28, height:28, border:'3px solid #ccc', borderTopColor:'var(--primary)',
                  borderRadius:'50%', animation:'spin 1s linear infinite'
                }}/>
              </div>
            )}
          </div>
          <style>{'@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}'}</style>
        </div>
      )}
      {/* toast подтверждение сохранения */}
      {!!okToast && (
        <div style={{position:'fixed', right:18, top:18, zIndex:3000, pointerEvents:'none'}} role="status" aria-live="polite">
          <div style={{background:'#1b5e20', color:'#fff', padding:'10px 14px', borderRadius:12, boxShadow:'0 8px 20px rgba(0,0,0,.2)'}}>
            {okToast}
          </div>
        </div>
      )}
      {!isNew && (
        <Actions style={{ marginBottom: 12 }}>
          <Link className="btn" to="/admin?tab=commercial-rent-properties">← К списку коммерческой аренды</Link>
        </Actions>
      )}

      {/* Основной блок всегда сверху и открыт */}
      <Card as="form" onSubmit={(e) => { e.preventDefault(); setConfirmOpen(true); }} style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>
            {isNew ? 'Создать коммерческую аренду' : `Редактировать коммерческую аренду #${id}`}
            {loadingForm ? ' — загрузка…' : ''}
          </h3>
          {/* Кнопка сохранить в верхней части */}
          <Actions style={{ margin: 0 }}>
            <Btn $kind="primary" type="button" disabled={saving} onClick={()=>setConfirmOpen(true)}>
              {isNew ? 'Создать' : 'Сохранить'}
            </Btn>
          </Actions>
        </div>
        {msg && (
          <div style={{ color: hasErrors(errors) ? '#e66' : 'var(--muted)', marginBottom: 8 }}>
            {msg}
          </div>
        )}

        <Row2>
          <Field>
            Название (RU)
            <input
              ref={refs.title_ru}
              value={form.title_ru}
              onChange={(e) => {
                const value = e.target.value;
                setForm(prev => ({ ...prev, title_ru: value }));
              }}
              onBlur={() => markTouched('title_ru')}
              aria-invalid={showErr('title_ru')}
              style={errStyle('title_ru')}
            />
            {showErr('title_ru') && <small style={{ color: '#e66' }}>{errors.title_ru}</small>}
          </Field>
          <Field>
            Название (EN)
            <input
              ref={refs.title_en}
              value={form.title_en}
              onChange={(e) => {
                const value = e.target.value;
                setForm(prev => ({ ...prev, title_en: value }));
              }}
              onBlur={() => markTouched('title_en')}
              aria-invalid={showErr('title_en')}
              style={errStyle('title_en')}
            />
          </Field>
        </Row2>

        {!isNew && (
          <Field>
            Slug (опционально)
            <input
              ref={refs.slug}
              value={form.slug}
              onChange={(e) => {
                const value = e.target.value;
                setForm(prev => ({ ...prev, slug: value }));
              }}
              onBlur={() => markTouched('slug')}
              placeholder="unique-slug"
              aria-invalid={false}
              style={{}}
            />
            <small>Можно оставить пустым — slug сгенерируется автоматически; уникальность проверит сервер.</small>
          </Field>
        )}

        {/* Застройщик */}
        <Row2>
          <Field>
            Застройщик (RU)
            <input
              value={form.developer_name_ru}
              onChange={(e)=>setForm(f=>({...f, developer_name_ru: e.target.value}))}
              placeholder="напр. ПИК"
            />
            <small>Если заполнено только это поле — будет записано в оба языка</small>
          </Field>
          <Field>
            Застройщик (EN)
            <input
              value={form.developer_name_en}
              onChange={(e)=>setForm(f=>({...f, developer_name_en: e.target.value}))}
              placeholder="e.g. PIK"
            />
          </Field>
        </Row2>

        <Row3>
          <Field>
            Цена аренды от (в месяц)
            <input
              ref={refs.price_base}
              type="number"
              value={form.price_base}
              onChange={(e) => {
                const value = e.target.value;
                setForm(prev => ({ ...prev, price_base: value }));
              }}
              onBlur={() => markTouched('price_base')}
              aria-invalid={showErr('price_base')}
              style={errStyle('price_base')}
            />
            {showErr('price_base') && <small style={{ color: '#e66' }}>{errors.price_base}</small>}
          </Field>
          <Field>
            Цена аренды до (в месяц)
            <input
              type="number"
              value={form.price_max}
              onChange={(e) => {
                const value = e.target.value;
                setForm(prev => ({ ...prev, price_max: value }));
              }}
              placeholder="Максимальная цена аренды"
            />
            <small>Опционально. Если не указано, будет показана только цена "от"</small>
          </Field>
          <Field>
            Валюта
            <select
              value={form.price_currency}
              onChange={(e) => {
                const value = e.target.value;
                setForm(prev => ({ ...prev, price_currency: value }));
              }}
            >
              {currencies.length > 0 ? (
                currencies.map(c => (
                  <option key={c.code} value={c.code}>
                    {c.code} ({c.symbol})
                  </option>
                ))
              ) : (
                <>
                  <option value="AED">AED</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="RUB">RUB</option>
                </>
              )}
            </select>
            <small>Цена аренды за месяц</small>
          </Field>
        </Row3>

        <Row3>
          <Field>
            Цена за м² от (в месяц)
            <input
              type="number"
              value={form.price_per_m2_min}
              onChange={(e) => {
                const value = e.target.value;
                setForm(prev => ({ ...prev, price_per_m2_min: value }));
              }}
              placeholder="Цена за м² от"
            />
            <small>Стоимость за квадратный метр в месяц (минимальная)</small>
          </Field>
          <Field>
            Цена за м² до (в месяц)
            <input
              type="number"
              value={form.price_per_m2_max}
              onChange={(e) => {
                const value = e.target.value;
                setForm(prev => ({ ...prev, price_per_m2_max: value }));
              }}
              placeholder="Цена за м² до"
            />
            <small>Стоимость за квадратный метр в месяц (максимальная)</small>
          </Field>
          <Field>
            
          </Field>
        </Row3>

        {/* Специфичные поля для коммерческой аренды */}
        <Card>
          <h3>Характеристики коммерческого помещения</h3>
          <Row3>
            <Field>
              Тип коммерческого помещения
              <select
                value={form.commercial_type}
                onChange={(e) => setForm({ ...form, commercial_type: e.target.value })}
              >
                <option value="">Выберите тип</option>
                <option value="office">Офис</option>
                <option value="retail">Торговое помещение</option>
                <option value="warehouse">Склад</option>
                <option value="restaurant">Ресторан/Кафе</option>
                <option value="showroom">Шоурум</option>
                <option value="medical">Медицинский центр</option>
                <option value="beauty">Салон красоты</option>
                <option value="other">Другое</option>
              </select>
            </Field>
            <Field>
              Высота потолков, м
              <input
                type="number"
                step="0.1"
                value={form.ceiling_height}
                onChange={(e) => setForm({ ...form, ceiling_height: e.target.value })}
                placeholder="3.0"
              />
            </Field>
            <Field>
              Парковочных мест
              <input
                type="number"
                value={form.parking_spaces}
                onChange={(e) => setForm({ ...form, parking_spaces: e.target.value })}
                placeholder="Количество мест"
              />
            </Field>
          </Row3>
          
          <Row3>
            <Field>
              Этаж от
              <input
                type="number"
                value={form.floor_min}
                onChange={(e) => setForm({ ...form, floor_min: e.target.value })}
                placeholder="1"
              />
            </Field>
            <Field>
              Этаж до
              <input
                type="number"
                value={form.floor_max}
                onChange={(e) => setForm({ ...form, floor_max: e.target.value })}
                placeholder="5"
              />
            </Field>
            <Field>
              Меблировано
              <div>
                <button type="button" className="btn" onClick={()=>setForm(f=>({...f, is_furnished:true}))} disabled={!!form.is_furnished}
                        style={form.is_furnished ? {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : undefined}>Да</button>{' '}
                <button type="button" className="btn" onClick={()=>setForm(f=>({...f, is_furnished:false}))} disabled={!form.is_furnished}
                        style={!form.is_furnished ? {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : undefined}>Нет</button>
              </div>
            </Field>
          </Row3>
        </Card>

        <Row3>
          <Field>
            Сдача (кв.)
            <input
              value={form.handover_quarter}
              onChange={(e) => setForm({ ...form, handover_quarter: e.target.value })}
              placeholder="Q4"
            />
          </Field>
          <Field>
            Сдача (год)
            <input
              type="number"
              value={form.handover_year}
              onChange={(e) => setForm({ ...form, handover_year: e.target.value })}
              placeholder="2026"
            />
          </Field>
          <Field>
            
          </Field>
        </Row3>

        {/* Диапазоны площадей */}
        <Card>
          <h3>Площади (опционально)</h3>
          <Row3>
            <Field>
              Площадь от, м²
              <input type="number" value={form.area_total_min} onChange={(e)=>setForm(f=>({...f, area_total_min: e.target.value}))} />
              <small>
                Текущий агрегат: {`{ min: ${ranges.area.min ?? '—'}, max: ${ranges.area.max ?? '—'} }`}
              </small>
            </Field>
            <Field>
              Площадь до, м²
              <input type="number" value={form.area_total_max} onChange={(e)=>setForm(f=>({...f, area_total_max: e.target.value}))} />
              <small>
                Текущий агрегат: {`{ min: ${ranges.area.min ?? '—'}, max: ${ranges.area.max ?? '—'} }`}
              </small>
            </Field>
            <Field>
              
            </Field>
          </Row3>
          <small style={{opacity:.7}}>Если не указывать — диапазоны будут вычисляться автоматически по юнитам.</small>
        </Card>

        <Row3>
          <Field>
            Регион
            <select
              ref={refs.region_id}
              value={form.region_id}
              onChange={(e) => {
                setForm({ ...form, region_id: e.target.value, district_id: '' });
                markTouched('region_id');
              }}
              onBlur={() => markTouched('region_id')}
              aria-invalid={showErr('region_id')}
              style={errStyle('region_id')}
              required
            >
              <option value="">— выберите —</option>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name || r.title || r.slug}
                </option>
              ))}
            </select>
            {showErr('region_id') && <small style={{ color: '#e66' }}>{errors.region_id}</small>}
          </Field>
          <Field>
            Район
            <select
              ref={refs.district_id}
              value={form.district_id}
              onChange={(e) => {
                setForm({ ...form, district_id: e.target.value });
                markTouched('district_id');
              }}
              onBlur={() => markTouched('district_id')}
              aria-invalid={showErr('district_id')}
              style={errStyle('district_id')}
              required
            >
              <option value="">— выберите —</option>
              {districtsForForm.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name || d.title || d.slug}
                </option>
              ))}
            </select>
            {showErr('district_id') && (
              <small style={{ color: '#e66' }}>{errors.district_id}</small>
            )}
          </Field>
          <Field></Field>
        </Row3>

        {/* Флаги */}
        <Card>
          <h3>Флаги</h3>
          <Row3>
            <Field>Активен
              <div>
                <button type="button" className="btn" onClick={()=>setForm(f=>({...f, is_active:true}))} disabled={form.is_active}
                        style={form.is_active ? {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : undefined}>Вкл</button>{' '}
                <button type="button" className="btn" onClick={()=>setForm(f=>({...f, is_active:false}))} disabled={!form.is_active}
                        style={!form.is_active ? {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : undefined}>Выкл</button>
              </div>
            </Field>
            <Field>Рассрочка
              <div>
                <button type="button" className="btn" onClick={()=>setForm(f=>({...f, installment_available:true}))} disabled={!!form.installment_available}
                        style={form.installment_available ? {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : undefined}>Да</button>{' '}
                <button type="button" className="btn" onClick={()=>setForm(f=>({...f, installment_available:false}))} disabled={!form.installment_available}
                        style={!form.installment_available ? {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : undefined}>Нет</button>
              </div>
            </Field>
            <Field>Инвестиционный
              <div>
                <button type="button" className="btn" onClick={async()=>{
                  if (isNew) { setForm(f=>({...f, is_investment:true})); return; }
                  try{ 
                    const result = await AdminAPI.setInvestmentCommercialRentProperty(Number(id), true); 
                    setForm(f=>({...f, is_investment:true})); 
                    setOkToast('Флаг "Инвестиционный" включён'); 
                    setTimeout(()=>setOkToast(''),2000);
                  }catch(e){
                    console.error('Ошибка установки флага инвестиционный:', e);
                    setMsg(e.message || 'Не удалось установить флаг "Инвестиционный"');
                    setTimeout(()=>setMsg(''), 3000);
                  }
                }} disabled={!!form.is_investment}
                   style={form.is_investment ? 
                     {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : 
                     {background:'#f8f9fa', color:'#6c757d', borderColor:'#dee2e6'}
                   }>Да</button>{' '}
                <button type="button" className="btn" onClick={async()=>{
                  if (isNew) { setForm(f=>({...f, is_investment:false})); return; }
                  try{ 
                    const result = await AdminAPI.setInvestmentCommercialRentProperty(Number(id), false); 
                    setForm(f=>({...f, is_investment:false})); 
                    setOkToast('Флаг "Инвестиционный" выключён'); 
                    setTimeout(()=>setOkToast(''),2000);
                  }catch(e){
                    console.error('Ошибка снятия флага инвестиционный:', e);
                    setMsg(e.message || 'Не удалось снять флаг "Инвестиционный"');
                    setTimeout(()=>setMsg(''), 3000);
                  }
                }} disabled={!form.is_investment}
                   style={!form.is_investment ? 
                     {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : 
                     {background:'#f8f9fa', color:'#6c757d', borderColor:'#dee2e6'}
                   }>Нет</button>
              </div>
            </Field>
          </Row3>

          <Row3>
            <Field>Рекомендуемый
              <div>
                <button type="button" className="btn" onClick={async()=>{
                  if (isNew) { setForm(f=>({...f, is_featured:true})); return; }
                  try{ 
                    const result = await AdminAPI.setFeaturedCommercialRentProperty(Number(id), true); 
                    setForm(f=>({...f, is_featured:true})); 
                    setOkToast('Флаг "Рекомендуемый" включён'); 
                    setTimeout(()=>setOkToast(''),2000);
                  }catch(e){
                    console.error('Ошибка установки флага рекомендуемый:', e);
                    setMsg(e.message || 'Не удалось установить флаг "Рекомендуемый"');
                    setTimeout(()=>setMsg(''), 3000);
                  }
                }} disabled={!!form.is_featured}
                   style={form.is_featured ? 
                     {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : 
                     {background:'#f8f9fa', color:'#6c757d', borderColor:'#dee2e6'}
                   }>Да</button>{' '}
                <button type="button" className="btn" onClick={async()=>{
                  if (isNew) { setForm(f=>({...f, is_featured:false})); return; }
                  try{ 
                    const result = await AdminAPI.setFeaturedCommercialRentProperty(Number(id), false); 
                    setForm(f=>({...f, is_featured:false})); 
                    setOkToast('Флаг "Рекомендуемый" выключён'); 
                    setTimeout(()=>setOkToast(''),2000);
                  }catch(e){
                    console.error('Ошибка снятия флага рекомендуемый:', e);
                    setMsg(e.message || 'Не удалось снять флаг "Рекомендуемый"');
                    setTimeout(()=>setMsg(''), 3000);
                  }
                }} disabled={!form.is_featured}
                   style={!form.is_featured ? 
                     {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : 
                     {background:'#f8f9fa', color:'#6c757d', borderColor:'#dee2e6'}
                   }>Нет</button>
              </div>
            </Field>

            <Field>Герой (на главной)
              <div>
                <button type="button" className="btn" onClick={async()=>{
                  if (isNew) { setForm(f=>({...f, is_homepage:true})); return; }
                  try{ 
                    const result = await AdminAPI.homepageSetCommercialRent(Number(id), true); 
                    setForm(f=>({...f, is_homepage:true})); 
                    setOkToast('Добавлен в герои'); 
                    setTimeout(()=>setOkToast(''),2000);
                  }catch(e){
                    console.error('Ошибка установки флага герой:', e);
                    setMsg(e.message || 'Не удалось добавить в герои');
                    setTimeout(()=>setMsg(''), 3000);
                  }
                }} disabled={!!form.is_homepage}
                   style={form.is_homepage ? 
                     {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : 
                     {background:'#f8f9fa', color:'#6c757d', borderColor:'#dee2e6'}
                   }>Да</button>{' '}
                <button type="button" className="btn" onClick={async()=>{
                  if (isNew) { setForm(f=>({...f, is_homepage:false})); return; }
                  try{ 
                    const result = await AdminAPI.homepageSetCommercialRent(Number(id), false); 
                    setForm(f=>({...f, is_homepage:false})); 
                    setOkToast('Снят из героев'); 
                    setTimeout(()=>setOkToast(''),2000);
                  }catch(e){
                    console.error('Ошибка снятия флага герой:', e);
                    setMsg(e.message || 'Не удалось снять из героев');
                    setTimeout(()=>setMsg(''), 3000);
                  }
                }} disabled={!form.is_homepage}
                   style={!form.is_homepage ? 
                     {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : 
                     {background:'#f8f9fa', color:'#6c757d', borderColor:'#dee2e6'}
                   }>Нет</button>
              </div>
            </Field>

            <Field>Статус
              <select value={form.status} onChange={e=>setForm(f=>({...f, status:e.target.value}))}>
                <option value="active">active</option>
                <option value="reserved">reserved</option>
                <option value="rented">rented</option>
                <option value="archived">archived</option>
              </select>
            </Field>
          </Row3>
        </Card>

        {/* словари */}
        <Row2>
          <Field>
            Тип объекта
            <select
              ref={refs.property_type_id}
              value={form.property_type_id}
              onChange={(e) => setForm({ ...form, property_type_id: e.target.value })}
              onBlur={() => markTouched('property_type_id')}
              aria-invalid={showErr('property_type_id')}
              style={errStyle('property_type_id')}
              required
            >
              <option value="">— выберите —</option>
              {propertyTypes.map((pt) => (
                <option key={pt.id} value={pt.id}>
                  {pt.name} — {pt.slug} [id:{pt.id}]
                </option>
              ))}
            </select>
            {showErr('property_type_id') && (
              <small style={{ color: '#e66' }}>{errors.property_type_id}</small>
            )}
          </Field>

          <Field>
            Тип/вариант сделки
            <select
              ref={refs.deal_type_id}
              value={form.deal_type_id}
              onChange={(e) => setForm({ ...form, deal_type_id: e.target.value })}
              onBlur={() => markTouched('deal_type_id')}
              aria-invalid={showErr('deal_type_id')}
              style={errStyle('deal_type_id')}
              required
            >
              <option value="">— выберите —</option>
              {dealTypes.map((dt) => (
                <option key={dt.id} value={dt.id}>
                  {dt.name} — {dt.slug} [id:{dt.id}]
                </option>
              ))}
            </select>
            {showErr('deal_type_id') && (
              <small style={{ color: '#e66' }}>{errors.deal_type_id}</small>
            )}
          </Field>
        </Row2>

        {/* Локация */}
        <h4 style={{ margin: '18px 0 8px' }}>Локация</h4>
        <Row3>
          <Field>
            Адрес
            <input
              ref={refs.address}
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              onBlur={() => markTouched('address')}
              aria-invalid={showErr('address')}
              style={errStyle('address')}
              placeholder="улица, дом, бизнес-центр"
              required
            />
            {showErr('address') && <small style={{ color: '#e66' }}>{errors.address}</small>}
          </Field>
          <Field>
            Широта (lat)
            <input
              ref={refs.lat}
              type="number" step="any"
              value={form.lat}
              onChange={(e) => setForm({ ...form, lat: e.target.value })}
              onBlur={() => markTouched('lat')}
              aria-invalid={showErr('lat')}
              style={errStyle('lat')}
              placeholder="55.751244"
            />
            {showErr('lat') && <small style={{ color: '#e66' }}>{errors.lat}</small>}
          </Field>
          <Field>
            Долгота (lng)
            <input
              ref={refs.lng}
              type="number" step="any"
              value={form.lng}
              onChange={(e) => setForm({ ...form, lng: e.target.value })}
              onBlur={() => markTouched('lng')}
              aria-invalid={showErr('lng')}
              style={errStyle('lng')}
              placeholder="37.618423"
            />
            {showErr('lng') && <small style={{ color: '#e66' }}>{errors.lng}</small>}
          </Field>
        </Row3>

        <MapPicker
          value={{
            lat: form.lat !== '' ? Number(form.lat) : undefined,
            lng: form.lng !== '' ? Number(form.lng) : undefined,
            address: form.address
          }}
          onChange={(v) => {
            setForm((f) => ({
              ...f,
              address: v.address ?? f.address,
              lat: v.lat ?? f.lat,
              lng: v.lng ?? f.lng,
            }));
          }}
          lang="ru_RU"
          height={360}
        />

        {/* Описание */}
        <h4 style={{ margin: '18px 0 8px' }}>Описание</h4>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
          <div className="badge">RU</div>
          <button
            type="button"
            className="btn"
            onClick={() => {
              const t = `<h3>Локация</h3><p>Удобное расположение в деловом центре города.</p>
<h3>Инфраструктура</h3><ul><li>Транспортная доступность</li><li>Парковка</li><li>Кафе и рестораны поблизости</li></ul>
<h3>Помещение</h3><p>Современная отделка, кондиционирование, высокие потолки.</p>
<h3>Условия аренды</h3><p>Гибкие условия. Возможна долгосрочная аренда.</p>`;
              setForm((f) => ({
                ...f,
                description_ru: (f.description_ru || '') + (f.description_ru ? '<br/><br/>' : '') + t,
              }));
            }}
          >
            Вставить шаблон
          </button>
          <button type="button" className="btn" onClick={() => setForm((f) => ({ ...f, description_ru: '' }))}>
            Очистить RU
          </button>
        </div>
        <RichTextWithTemplates
          propertyType="commercial"
          value={form.description_ru}
          onChange={(v) => setForm((f) => ({ ...f, description_ru: v }))}
          placeholder="Русское описание…"
        />

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '14px 0 8px' }}>
          <div className="badge">EN</div>
          <button
            type="button"
            className="btn"
            onClick={() => {
              const t = `<h3>Location</h3><p>Convenient location in the business center of the city.</p>
<h3>Infrastructure</h3><ul><li>Transport accessibility</li><li>Parking</li><li>Cafes and restaurants nearby</li></ul>
<h3>Space</h3><p>Modern finishing, air conditioning, high ceilings.</p>
<h3>Rental terms</h3><p>Flexible terms. Long-term rental possible.</p>`;
              setForm((f) => ({
                ...f,
                description_en: (f.description_en || '') + (f.description_en ? '<br/><br/>' : '') + t,
              }));
            }}
          >
            Insert template
          </button>
          <button type="button" className="btn" onClick={() => setForm((f) => ({ ...f, description_en: '' }))}>
            Clear EN
          </button>
        </div>
        <RichTextWithTemplates
          propertyType="commercial"
          value={form.description_en}
          onChange={(v) => setForm((f) => ({ ...f, description_en: v }))}
          placeholder="English description…"
        />

        {/* SEO блок */}
        <h4 style={{ margin: '18px 0 8px' }}>SEO</h4>
        <Row2>
          <Field>
            Meta title RU
            <input value={form.meta_title_ru} onChange={e=>setForm(f=>({...f, meta_title_ru: e.target.value}))} />
          </Field>
          <Field>
            Meta title EN
            <input value={form.meta_title_en} onChange={e=>setForm(f=>({...f, meta_title_en: e.target.value}))} />
          </Field>
        </Row2>

        <Row2>
          <Field>
            Meta description RU
            <textarea rows={3} value={form.meta_description_ru} onChange={e=>setForm(f=>({...f, meta_description_ru: e.target.value}))} />
          </Field>
          <Field>
            Meta description EN
            <textarea rows={3} value={form.meta_description_en} onChange={e=>setForm(f=>({...f, meta_description_en: e.target.value}))} />
          </Field>
        </Row2>

        <Actions>
          {/* Дублированная кнопка сохранения внизу формы */}
          <Btn $kind="primary" type="button" disabled={saving} onClick={()=>setConfirmOpen(true)}>
            {isNew ? 'Создать' : 'Сохранить'}
          </Btn>
          <Btn type="button" onClick={() => nav('/admin/commercial-rent')}>Отмена</Btn>
        </Actions>
        {!!okToast && (
          <div style={{marginTop:10, color:'#1b5e20'}} aria-live="polite">{okToast}</div>
        )}
      </Card>

      {/* Модалка подтверждения сохранения */}
      {confirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{ position:'fixed', inset:0, zIndex:3500, background:'rgba(0,0,0,.35)', display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={()=>setConfirmOpen(false)}
        >
          <div
            style={{ background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:16, boxShadow:'var(--shadow)', padding:18, minWidth:320 }}
            onClick={(e)=>e.stopPropagation()}
          >
            <div style={{fontWeight:800, marginBottom:8}}>{isNew ? 'Создать коммерческую аренду?' : 'Сохранить изменения?'}</div>
            <div style={{opacity:.8, fontSize:13, marginBottom:12}}>Подтвердите действие или отмените.</div>
            <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
              <Btn onClick={()=>setConfirmOpen(false)}>Отмена</Btn>
              <Btn $kind="primary" onClick={()=>{ setConfirmOpen(false); save(); }}>Да, {isNew ? 'создать' : 'сохранить'}</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Остальные секции: скрываем при создании, показываем после сохранения */}
      {isNew ? (
        <Card>
          <div style={{ color:'#777' }}>
            После создания объекта вы сможете добавить фотографии, удобства, планировки, условия оплаты и назначить менеджеров.
          </div>
        </Card>
      ) : (
        <div style={{display:'grid', gap:12, marginTop:12}}>
          <Collapse title="Условия оплаты" defaultOpen={true}>
            <SpecializedPaymentPlansPanel propertyId={Number(id)} propertyType="commercial-rent" />
          </Collapse>
          
          <Collapse title="Удобства объекта" defaultOpen={true}>
            <SpecializedAmenitiesPanel propertyId={Number(id)} propertyType="commercial-rent" />
          </Collapse>
          
          <Collapse title="Планировки" defaultOpen={true}>
            <SpecializedUnitsPanel propertyId={Number(id)} propertyType="commercial-rent" />
          </Collapse>
          
          <Collapse title="Менеджеры" defaultOpen={true}>
            <SpecializedManagersPanel propertyId={Number(id)} propertyType="commercial-rent" />
          </Collapse>
          
          <Collapse title="Фотографии" defaultOpen={true}>
            <SpecializedPropertyMediaManager propertyId={Number(id)} propertyType="commercial-rent" />
          </Collapse>
        </div>
      )}
    </>
  );
}